import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getSteps,
  getCourse,
  getCourses,
  createCourse,
  updateCourse,
} from '../api/courses.api';
import {
  IStep,
  IGetSteps,
  ICourse,
  IGetCourse,
  IGetCourses,
} from '../models/courses.model';

interface ICourseState {
  steps?: IStep[];
  course?: ICourse;
  courses?: ICourse[];
  stepLoading?: boolean;
  courseLoading?: boolean;
  error?: AxiosError | string;
}

const initialState: ICourseState = {
  steps: [],
  course: undefined,
  courses: [],
  stepLoading: false,
  courseLoading: false,
};

const courseSlicer = createSlice({
  name: 'course',
  initialState,
  reducers: {
    getStepsStart: (state: ICourseState) => {
      state.steps = [];
      state.stepLoading = true;
      state.error = undefined;
    },
    getStepsSuccess: (
      state: ICourseState,
      action: PayloadAction<IGetSteps>,
    ) => {
      state.steps = action.payload.data;
      state.stepLoading = false;
      state.error = undefined;
    },
    getStepsFailure: (
      state: ICourseState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.steps = [];
      state.stepLoading = false;
      state.error = action.payload;
    },
    getCourseStart: (state: ICourseState) => {
      state.course = undefined;
      state.courseLoading = true;
      state.error = undefined;
    },
    getCourseSuccess: (
      state: ICourseState,
      action: PayloadAction<IGetCourse>,
    ) => {
      state.course = action.payload.data;
      state.courseLoading = false;
      state.error = undefined;
    },
    getCourseFailure: (
      state: ICourseState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.course = undefined;
      state.courseLoading = false;
      state.error = action.payload;
    },
    getCoursesStart: (state: ICourseState) => {
      state.courses = [];
      state.courseLoading = true;
      state.error = undefined;
    },
    getCoursesSuccess: (
      state: ICourseState,
      action: PayloadAction<IGetCourses>,
    ) => {
      state.courses = action.payload.data;
      state.courseLoading = false;
      state.error = undefined;
    },
    getCoursesFailure: (
      state: ICourseState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.courses = [];
      state.courseLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ICourseState) => {
      state.courseLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ICourseState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.courseLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ICourseState) => {
      state.courseLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ICourseState) => {
      state.courseLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getStepsStart,
  getStepsSuccess,
  getStepsFailure,
  getCourseStart,
  getCourseSuccess,
  getCourseFailure,
  getCoursesStart,
  getCoursesSuccess,
  getCoursesFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
} = courseSlicer.actions;

export default courseSlicer.reducer;

export const doGetSteps = (): any => async (dispatch: any) => {
  try {
    dispatch(getStepsStart());
    const response = await getSteps();
    dispatch(getStepsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getStepsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetCourse =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getCourseStart());
      const response = await getCourse(id);
      dispatch(getCourseSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getCourseFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetCourses = (): any => async (dispatch: any) => {
  try {
    dispatch(getCoursesStart());
    const response = await getCourses();
    dispatch(getCoursesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getCoursesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateCourse =
  (data: ICourse): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createCourse(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateCourse =
  (id: string, data: ICourse): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateCourse(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
